import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  api,
  MachineryOutcome,
  type MachineryImportSummary,
  type MachineryImportRow,
} from '../api/client'
import { invalidateMachinery } from '../api/invalidate'
import { requireAccessToken } from '../auth/msal'
import './MachineryImport.css'

const OUTCOME_LABEL: Record<number, string> = {
  [MachineryOutcome.Imported]: 'Added',
  [MachineryOutcome.ImportedWithWarnings]: 'Added, with notes',
  [MachineryOutcome.Rejected]: 'Rejected',
  [MachineryOutcome.Updated]: 'Updated',
  [MachineryOutcome.Ambiguous]: 'Left alone',
}

/**
 * Loading the machinery half of an Asset List workbook.
 *
 * Deliberately a separate screen from `/import`, which reads a completely
 * different sheet: that one needs a registration column and seven date columns,
 * and this one has neither. Sharing a screen would mean a mode switch on an
 * upload form, which is how somebody loads 562 machines into the vehicle register.
 */
export function MachineryImport() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [file, setFile] = useState<File | null>(null)
  const [summary, setSummary] = useState<MachineryImportSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  const upload = useMutation({
    mutationFn: async (chosen: File) => {
      // At the top of the click, while the gesture's activation is still live.
      await requireAccessToken()
      return api.importMachinery(chosen)
    },
    onSuccess: async (result) => {
      setSummary(result)
      setError(null)
      await invalidateMachinery(queryClient)
    },
    onError: (e: Error) => setError(e.message),
  })

  const report = useQuery({
    queryKey: ['machinery-import-report', summary?.importBatchId],
    queryFn: () => api.getMachineryImportReport(summary!.importBatchId),
    enabled: summary?.importBatchId != null,
  })

  return (
    <div className="mimp">
      <button type="button" className="mimp__back" onClick={() => navigate('/machinery')}>
        ← Machinery
      </button>

      <header className="mimp__head">
        <h1>Import the asset list</h1>
        <p>
          Reads the <b>Asset List</b> sheet and loads every row marked{' '}
          <b>MACHINERY</b>. The vehicle rows in the same sheet are left alone —
          they belong to the register, and that has its own import.
        </p>
      </header>

      {!summary && (
        <>
          <div className="mimp__drop">
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null)
                setError(null)
              }}
              aria-label="Choose an .xlsx workbook"
            />
            <p>{file ? file.name : 'Choose an .xlsx workbook'}</p>
          </div>

          {/*
            Said before the button, not after the fact. An import that overwrites
            600 records is worth understanding first.
          */}
          <ul className="mimp__rules">
            <li>
              <b>Every row is accounted for.</b> One outcome per source row, including
              the ones that import cleanly.
            </li>
            <li>
              <b>Importing the same file twice is safe.</b> A machine is recognised by
              its asset code and serial together, so a second run updates rather than
              duplicates.
            </li>
            <li>
              <b>A blank stays blank.</b> Nothing here fills in a missing year or
              invents an asset code; the gaps are listed instead.
            </li>
            <li>
              <b>Removed machines stay removed.</b> A re-import never brings back
              something somebody deleted on purpose.
            </li>
            <li>
              <b>No site is created.</b> The asset list names sites the register does
              not know, and three of them are statuses rather than places. The name is
              kept on the machine; the shared site list is not touched.
            </li>
          </ul>

          {error && (
            <p className="mimp__error" role="alert">
              {error}
            </p>
          )}

          <button
            type="button"
            className="btn btn--primary"
            disabled={!file || upload.isPending}
            onClick={() => file && upload.mutate(file)}
          >
            {upload.isPending ? 'Reading…' : 'Import machinery'}
          </button>
        </>
      )}

      {summary && (
        <>
          <div className="mimp__tallies">
            <Tally label="Rows read" value={summary.totalSourceRows} />
            <Tally label="Added" value={summary.created} />
            <Tally label="Updated" value={summary.updated} />
            <Tally label="Left alone" value={summary.ambiguous} tone={summary.ambiguous ? 'warn' : undefined} />
            <Tally label="Rejected" value={summary.rejected} tone={summary.rejected ? 'crit' : undefined} />
            <Tally label="Categories added" value={summary.categoriesCreated} />
          </div>

          {summary.needsAttention > 0 && (
            <p className="mimp__note">
              <b>{summary.needsAttention}</b> of these machines are missing something
              that identifies or dates them — an asset code, a serial, a make or a
              year.{' '}
              <Link to="/machinery?needsAttention=1">See them on the register →</Link>
            </p>
          )}

          {summary.ambiguous > 0 && (
            <p className="mimp__note mimp__note--warn">
              <b>{summary.ambiguous}</b> rows share an asset code and serial with
              another machine already on the register, so nothing was written for
              them. Giving each one a code of its own is what unblocks them —
              guessing which machine a row meant is not something this import will
              do.
            </p>
          )}

          <h2 className="mimp__reporth">Row by row</h2>

          {report.isPending && <p className="mimp__loading">Loading the report…</p>}

          {report.data && (
            <ul className="mimp__report">
              {report.data
                // The clean rows are counted above; listing 550 "Added" lines would
                // bury the handful that need reading.
                .filter((r) => r.outcome !== MachineryOutcome.Imported)
                .map((row) => (
                  <ReportRow key={row.sourceRowNumber} row={row} />
                ))}

              {report.data.every((r) => r.outcome === MachineryOutcome.Imported) && (
                <li className="mimp__clean">Every row imported cleanly.</li>
              )}
            </ul>
          )}

          <button type="button" className="btn btn--primary" onClick={() => navigate('/machinery')}>
            Open the machinery register →
          </button>
        </>
      )}
    </div>
  )
}

function Tally({ label, value, tone }: { label: string; value: number; tone?: 'warn' | 'crit' }) {
  return (
    <div className={`mimp__tally${tone ? ` mimp__tally--${tone}` : ''}`}>
      <b>{value.toLocaleString('en-IN')}</b>
      <span>{label}</span>
    </div>
  )
}

function ReportRow({ row }: { row: MachineryImportRow }) {
  return (
    <li>
      <span className="mimp__row">row {row.sourceRowNumber}</span>
      <span className="mimp__label">{OUTCOME_LABEL[row.outcome] ?? 'Unknown'}</span>
      <span className="mimp__what">
        {row.sourceLabel ? <b>{row.sourceLabel}</b> : <em>unnamed</em>}
        {row.reason && <span className="mimp__reason">{row.reason}</span>}
      </span>
    </li>
  )
}
